import React                 from 'react';
import View                  from '@widesk-ui/components/View';
import TriStateSwitch        from '@/TokamapEditor/ux/TriStateSwitch';
import Field                 from '@/TokamapEditor/ux/AreaSidebar/Field';
import SliderInputNumber     from '@/TokamapEditor/ux/AreaSidebar/SliderInputNumber';
import NormalHoverToggle     from '@/TokamapEditor/ux/AreaSidebar/NormalHoverToggle';
import { NormalHoverScope }  from '@/TokamapEditor/ux/AreaSidebar/NormalHoverToggle';
import PositionGrid          from '@/TokamapEditor/ux/AreaSidebar/PositionGrid';
import OrientationField      from '@/TokamapEditor/ux/AreaSidebar/OrientationField';
import { orientationModeOf } from '@/TokamapEditor/ux/AreaSidebar/orientation';
import { OrientationValues } from '@/TokamapEditor/ux/AreaSidebar/orientation';
import useDesignTokens       from '@/hooks/useDesignTokens';

const SCOPE_NOTES: Record<NormalHoverScope, string> = {
	normal: `Les réglages ci-dessous s'appliquent à l'état normal.`,
	hover: `Les réglages ci-dessous s'appliquent quand le visiteur survole la forme.`,
};

/** Bascule Normal / Au survol, commune aux onglets du thème, avec la note qui va avec. */
export const ScopeToggle = ({ value, onChange }: {
	value: NormalHoverScope;
	onChange: (value: NormalHoverScope) => void;
}) => <NormalHoverToggle value={value} onChange={onChange} help={SCOPE_NOTES[value]} />;

/** Un réglage que le thème impose à ses formes, ou laisse à chacune. */
export const ImposedField = ({ field, label, imposed, onImpose, onRelease, children }: {
	field: string;
	label: string;
	imposed: boolean;
	onImpose: () => void;
	onRelease: () => void;
	children: React.ReactNode;
}) => {
	const tokens = useDesignTokens();

	return (
		<div data-theme-field={field} style={{ borderRadius: 8 }}>
			<View
				style={{
					borderRadius: 8,
					border: `1px solid ${imposed ? `color-mix(in srgb, ${tokens.color.primary} 40%, transparent)` : tokens.border.subtle}`,
					background: imposed ? `color-mix(in srgb, ${tokens.color.primary} 5%, transparent)` : undefined,
				}}
			>
				<View row spread centerV gap="xs" style={{ padding: '8px 10px' }}>
					<View gap={2}>
						<View size={13} style={{ fontWeight: 600 }}>{label}</View>
						<View size={11.5} color={imposed ? tokens.color.primary : tokens.text.muted}>
							{imposed ? 'Imposé aux formes liées' : 'Propre à chaque forme'}
						</View>
					</View>

					<TriStateSwitch value={imposed} onChange={checked => (checked ? onImpose() : onRelease())} />
				</View>

				{imposed && <View style={{ padding: '2px 10px 10px' }}>{children}</View>}
			</View>
		</div>
	);
};

/**
 * Curseur d'un réglage de thème : la valeur suit le glissé localement et n'est enregistrée qu'au
 * relâchement, sans quoi chaque frame partirait en requête. L'état local est relâché ensuite :
 * `themeStore` applique la valeur de façon optimiste, et un échec réaffiche l'ancienne.
 */
export const ThemeSlider = ({ value, onSave, ...props }: Omit<React.ComponentProps<typeof SliderInputNumber>, 'value' | 'onChange' | 'onAfterChange'> & {
	value: number;
	onSave: (value: number) => Promise<void>;
}) => {
	const [dragging, setDragging] = React.useState<number>();
	const fallback = props.min ?? 0;

	return (
		<SliderInputNumber
			{...props}
			value={dragging ?? value}
			onChange={v => setDragging(Number(v ?? fallback))}
			onAfterChange={async v => {
				const next = Number(v ?? fallback);

				setDragging(next);
				await onSave(next);
				setDragging(undefined);
			}}
		/>
	);
};

/** Curseur en pourcentage d'une part comprise entre 0 et 1. */
export const ThemeShareSlider = ({ value, onSave, factor = 100, max = 100 }: {
	value: number;
	onSave: (share: number) => Promise<void>;
	factor?: number;
	max?: number;
}) => (
	<ThemeSlider
		value={Math.round(value * factor)}
		min={0}
		max={max}
		step={1}
		suffix="%"
		onSave={percent => onSave(percent / factor)}
	/>
);

type Position = { horizontalAlign?: number; verticalAlign?: number; };

/** Position imposée dans la forme : la grille pour les neuf places usuelles, les curseurs pour le reste. */
export const ThemePositionField = ({ value, onChange }: {
	value: Position;
	onChange: (position: Position) => Promise<void>;
}) => {
	const horizontal = value.horizontalAlign ?? 0.5;
	const vertical = value.verticalAlign ?? 0.5;

	return (
		<ImposedField
			field="position"
			label="Position"
			imposed={value.horizontalAlign !== undefined || value.verticalAlign !== undefined}
			onImpose={() => onChange({ horizontalAlign: 0.5, verticalAlign: 0.5 })}
			onRelease={() => onChange({ horizontalAlign: undefined, verticalAlign: undefined })}
		>
			<View gap="sm">
				<PositionGrid
					horizontal={horizontal}
					vertical={vertical}
					onPick={(horizontalAlign, verticalAlign) => onChange({ horizontalAlign, verticalAlign })}
				/>

				<Field label="Horizontale">
					<ThemeShareSlider value={horizontal} onSave={horizontalAlign => onChange({ horizontalAlign, verticalAlign: vertical })} />
				</Field>

				<Field label="Verticale">
					<ThemeShareSlider value={vertical} onSave={verticalAlign => onChange({ horizontalAlign: horizontal, verticalAlign })} />
				</Field>
			</View>
		</ImposedField>
	);
};

/**
 * Orientation imposée. « Suit la carte » s'écrit `rotateWithMap: false` plutôt que par deux champs
 * vides, qui laisseraient à chaque forme la sienne.
 */
export const ThemeOrientationField = ({ value, onChange, readable }: {
	value: OrientationValues;
	onChange: (values: OrientationValues) => Promise<void>;
	readable?: boolean;
}) => {
	const imposed = value.rotation !== undefined || value.rotateWithMap !== undefined || value.keepReadable !== undefined;
	const write = (values: OrientationValues) => onChange({
		rotation: values.rotation,
		rotateWithMap: values.rotateWithMap ?? false,
		...(readable ? { keepReadable: values.keepReadable ?? false } : {}),
	});

	return (
		<ImposedField
			field="rotation"
			label="Orientation"
			imposed={imposed}
			onImpose={() => write({})}
			onRelease={() => onChange({ rotation: undefined, rotateWithMap: undefined, ...(readable ? { keepReadable: undefined } : {}) })}
		>
			<OrientationField
				readable={readable}
				mode={orientationModeOf(value)}
				effectiveRotation={value.rotation ?? 0}
				onChange={write}
				slider={(
					<ThemeSlider
						value={value.rotation ?? 0}
						min={0}
						max={359}
						step={1}
						suffix="°"
						onSave={rotation => write({ rotation })}
					/>
				)}
			/>
		</ImposedField>
	);
};
